import React from "react";
import styled from "styled-components";
import { Popup } from "semantic-ui-react";
import { Text } from "components/shared";
import { TURBINE_DATA_TIMEOUT_MS } from "../../constants";

// CCI link + DSO curtailment indicators for every PF2 turbine, derived from
// the board's CEI 016 status_word (see cei016_logic.cpp's / EBS_PF2's
// dso_logic.cpp's ST_* bits) - works with every PF2 firmware that publishes
// status_word/power_cmd/nominal_power: Vestas-RCS-PIO via its schema, EBS+PF2
// (any schema) from 10.9 on, passed through by selectors/liveParams.js.
const ST_REMOTE_ALLOWED = 1 << 1;
const ST_DSO_LIMIT_ACTIVE = 1 << 3;
const ST_DSO_MOD_ACTIVE = 1 << 4;
const ST_DSO_CHANNEL_OK = 1 << 6; // CCI connected with a valid DSO heartbeat (HR 40008)

const COLORS = {
  green: "#21ba45",
  orange: "#f2711c",
  red: "#db2828",
  grey: "#767676",
};

const Row = styled.div`
  display: flex;
  justify-content: ${(props) => props.justify};
  gap: ${(props) => (props.compact ? "1rem" : "1.5rem")};
  width: ${(props) => (props.compact ? "auto" : "100%")};
  margin-bottom: ${(props) => (props.compact ? "0" : "0.5rem")};
`;

const Indicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  cursor: default;
`;

const Dot = styled.span`
  display: inline-block;
  width: ${(props) => (props.compact ? "10px" : "12px")};
  height: ${(props) => (props.compact ? "10px" : "12px")};
  border-radius: 50%;
  background-color: ${(props) => props.color};
  box-shadow: 0 0 6px ${(props) => props.color};
`;

const Label = styled((props) => <Text {...props} />)`
  color: ${(props) => props.theme.textDark};
  font-weight: 600;
`;

const formatKw = (kw) => `${Math.round(kw)} kW`;

// Fresh telemetry from the board itself: CCI/DSO live on the PF2 board, so
// they stay valid with the PLC's Modbus down (board keeps publishing them -
// EBS+PF2 10.9+) - only the board's own link and data freshness count.
export const isBoardDataFresh = (turbineState) =>
  !!turbineState &&
  turbineState.online !== false &&
  !!turbineState.lastUpdate &&
  Date.now() - turbineState.lastUpdate < TURBINE_DATA_TIMEOUT_MS;

// Dashboard card header/list (compact): turbines without status_word (not
// PF2) show nothing at all; a PF2 turbine with stale data shows "—" rather
// than a red "offline" - we simply don't know the CCI/DSO state then.
const Unknown = ({ compact, justify, tip }) => (
  <Popup
    content={tip}
    position="top center"
    trigger={
      <Row compact={compact ? 1 : 0} justify={justify}>
        <Indicator>
          <Label size={13}>CCI —</Label>
        </Indicator>
        <Indicator>
          <Label size={13}>DSO —</Label>
        </Indicator>
      </Row>
    }
  />
);

const CciDsoStatus = ({ liveProps, connected, compact, justify = "center" }) => {
  const statusWord = Number(liveProps.status_word);
  if (liveProps.status_word === undefined || !Number.isFinite(statusWord)) return null;
  if (compact && !connected) {
    return <Unknown compact={1} justify={justify} tip="No recent data from the turbine" />;
  }

  const powerCmd = Number(liveProps.power_cmd);
  const nominal = Number(liveProps.nominal_power);

  const cciOk = (statusWord & ST_DSO_CHANNEL_OK) !== 0;
  const remoteAllowed = (statusWord & ST_REMOTE_ALLOWED) !== 0;
  // "DSO active" = the DSO is actually curtailing right now (user choice
  // 2026-10-05): an explicit DSO limit/modulation stage intervening, OR the
  // CCI's RemotePower % (which becomes the cascade's base, with no ST_ bit
  // of its own) bringing the command below Pmax while CCI has control.
  // OV and italscada's own power limit are not DSO, so not counted.
  const remoteBelowPmax =
    remoteAllowed &&
    Number.isFinite(powerCmd) &&
    Number.isFinite(nominal) &&
    nominal > 0 &&
    powerCmd < nominal - 0.5;
  const dsoActive =
    (statusWord & (ST_DSO_LIMIT_ACTIVE | ST_DSO_MOD_ACTIVE)) !== 0 || remoteBelowPmax;

  let cciColor = cciOk ? COLORS.green : COLORS.red;
  let cciTip = cciOk
    ? "CCI connected - DSO heartbeat OK"
    : "CCI not connected - no valid DSO heartbeat";
  let dsoColor = dsoActive ? COLORS.orange : COLORS.grey;
  let dsoTip = dsoActive
    ? `DSO is limiting power to ${formatKw(powerCmd)}` +
      (Number.isFinite(nominal) && nominal > 0 ? ` of ${formatKw(nominal)}` : "")
    : "DSO not limiting - turbine allowed up to Pmax";

  // No fresh telemetry: last-known bits may be stale, don't show them as live.
  if (!connected) {
    cciColor = COLORS.grey;
    dsoColor = COLORS.grey;
    cciTip = "No data from the turbine";
    dsoTip = "No data from the turbine";
  }

  const labelSize = compact ? 13 : 15;

  return (
    <Row compact={compact ? 1 : 0} justify={justify}>
      <Popup
        content={cciTip}
        position="top center"
        trigger={
          <Indicator>
            <Dot compact={compact ? 1 : 0} color={cciColor} />
            <Label size={labelSize}>CCI</Label>
          </Indicator>
        }
      />
      <Popup
        content={dsoTip}
        position="top center"
        trigger={
          <Indicator>
            <Dot compact={compact ? 1 : 0} color={dsoColor} />
            <Label size={labelSize}>
              {connected && dsoActive && Number.isFinite(powerCmd)
                ? `DSO ${formatKw(powerCmd)}`
                : "DSO"}
            </Label>
          </Indicator>
        }
      />
    </Row>
  );
};

export default CciDsoStatus;
